/**
 * Platform Registry for managing cross-platform code generation
 */

import type { ComponentConfig } from "../main-index";
import type { ParsedModule } from "../parser";
import type { ScannedModule } from "../scanner";
import { AndroidPlatformGenerator } from "./android-platform";
import { IOSPlatformGenerator } from "./ios-platform";
import type {
  AndroidPlatformGeneratorInterface,
  IOSPlatformGeneratorInterface,
  PlatformGenerator,
  SupportedPlatform,
} from "./platform-interface";
import {
  isAndroidPlatformGenerator,
  isIOSPlatformGenerator,
} from "./platform-interface";

export class PlatformRegistry {
  private generators = new Map<SupportedPlatform, PlatformGenerator>();
  private scannedModules: ScannedModule[] = [];

  constructor(config: ComponentConfig) {
    // Register iOS platform (wraps existing BridgeGenerator)
    this.generators.set("ios", new IOSPlatformGenerator(config));

    // Register Android platform (placeholder implementation)
    this.generators.set("android", new AndroidPlatformGenerator(config));
  }

  /**
   * Set scanned modules to be passed to platform generators
   */
  setScannedModules(modules: ScannedModule[]): void {
    this.scannedModules = modules;
  }

  /**
   * Get platform generator by name
   */
  getGenerator(platform: SupportedPlatform): PlatformGenerator | undefined {
    return this.generators.get(platform);
  }

  /**
   * Get platform generator by name (alias for getGenerator)
   */
  getPlatform(platform: SupportedPlatform): PlatformGenerator | undefined {
    return this.generators.get(platform);
  }

  /**
   * Get iOS platform generator with proper typing
   */
  getIOSPlatform(): IOSPlatformGeneratorInterface | undefined {
    const generator = this.generators.get("ios");
    return generator && isIOSPlatformGenerator(generator)
      ? generator
      : undefined;
  }

  /**
   * Get Android platform generator with proper typing
   */
  getAndroidPlatform(): AndroidPlatformGeneratorInterface | undefined {
    const generator = this.generators.get("android");
    return generator && isAndroidPlatformGenerator(generator)
      ? generator
      : undefined;
  }

  /**
   * Get all available platforms
   */
  getAvailablePlatforms(): SupportedPlatform[] {
    return Array.from(this.generators.keys()).filter((platform) => {
      const generator = this.generators.get(platform);
      return generator?.isAvailable() ?? false;
    });
  }

  /**
   * Generate bridge code for specific platform
   */
  async generateForPlatform(
    platform: SupportedPlatform,
    modules: ParsedModule[],
  ): Promise<string[]> {
    const generator = this.generators.get(platform);

    if (!generator) {
      throw new Error(`Platform '${platform}' is not supported`);
    }

    if (!generator.isAvailable()) {
      console.warn(
        `Platform '${platform}' is not available in current environment`,
      );
      return [];
    }

    return await generator.generateBridge(modules, this.scannedModules);
  }

  /**
   * Generate bridge code for all available platforms
   */
  async generateForAllPlatforms(
    modules: ParsedModule[],
  ): Promise<{ [platform: string]: string[] }> {
    const results: { [platform: string]: string[] } = {};
    const availablePlatforms = this.getAvailablePlatforms();

    for (const platform of availablePlatforms) {
      try {
        const files = await this.generateForPlatform(platform, modules);
        if (files.length > 0) {
          results[platform] = files;
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : String(error);
        console.warn(
          `Failed to generate for platform '${platform}':`,
          errorMessage,
        );
      }
    }

    return results;
  }

  /**
   * Check if a platform is supported
   */
  supports(platform: string): boolean {
    const targetPlatform = platform.toLowerCase() as SupportedPlatform;
    const generator = this.generators.get(targetPlatform);
    return generator?.supports(platform) ?? false;
  }

  /**
   * Check if a platform is available
   */
  isPlatformAvailable(platform: string): boolean {
    const targetPlatform = platform.toLowerCase() as SupportedPlatform;
    const generator = this.generators.get(targetPlatform);
    return generator?.isAvailable() ?? false;
  }

  /**
   * Get platform generator by name
   */
  getPlatformGenerator(platform: string): PlatformGenerator | undefined {
    const targetPlatform = platform.toLowerCase() as SupportedPlatform;
    return this.generators.get(targetPlatform);
  }
}
